/**
 * Geração da Lista de Partes em LaTeX
 */

import { Risco } from '../core/latex-types';
import { escapeLatex } from '../core/latex-utils';

/**
 * Determina o ícone baseado no tipo de perigo
 */
function obterIcone(tipoPerigo?: string): string {
  if (!tipoPerigo) return 'risco.png';

  const tipoLower = tipoPerigo.toLowerCase().trim();
  if (tipoLower.includes('vapor') || tipoLower.includes('steam') || tipoLower.includes('temperatura')) {
    return 'steam.png';
  }
  if (tipoLower.includes('pessoas') || tipoLower.includes('ergonômico') || tipoLower.includes('ergonomico')) {
    return 'pessoas.png';
  }
  return 'risco.png';
}

/**
 * Determina a cor e classificação baseada no HRN - Tabela Cliente (Arcelor Mittal)
 */
function obterClassificacao(hrn?: number): {
  classificacao: string;
  corFundo: string;
  corTexto: string;
} {
  if (!hrn || hrn < 1) {
    return {
      classificacao: 'Aceitável',
      corFundo: 'meuVerde',
      corTexto: 'white'
    };
  }

  if (hrn >= 1000) {
    return {
      classificacao: 'Inaceitável',
      corFundo: 'meuLaranja',
      corTexto: 'white'
    };
  } else if (hrn >= 500) {
    return {
      classificacao: 'Extremo',
      corFundo: 'meuLaranja',
      corTexto: 'white'
    };
  } else if (hrn >= 100) {
    return {
      classificacao: 'Muito alto',
      corFundo: 'meuLaranja',
      corTexto: 'white'
    };
  } else if (hrn >= 50) {
    return {
      classificacao: 'Alto',
      corFundo: 'meuAmarelo',
      corTexto: 'black'
    };
  } else if (hrn >= 10) {
    return {
      classificacao: 'Significante',
      corFundo: 'meuAmarelo',
      corTexto: 'black'
    };
  } else if (hrn >= 5) {
    return {
      classificacao: 'Baixo',
      corFundo: 'meuAmarelo',
      corTexto: 'black'
    };
  } else {
    return {
      classificacao: 'Muito baixo',
      corFundo: 'meuVerde',
      corTexto: 'white'
    };
  }
}

/**
 * Gera a Lista de Partes em LaTeX
 * @param riscos Array de riscos
 * @param referencia Referência para numeração (No Emitente ou SISMANA)
 */
export function gerarListaPartes(riscos: Risco[], referencia: string = ''): string {
  // Agrupar riscos por parte
  const riscosPorParte = new Map<string, Risco[]>();

  riscos.forEach(risco => {
    const parte = risco.parte || 'Sem parte definida';
    if (!riscosPorParte.has(parte)) {
      riscosPorParte.set(parte, []);
    }
    riscosPorParte.get(parte)!.push(risco);
  });

  let latex = `\\newpage

\\label{sec:checklist}

% Definir tipos de coluna para classificação
\\newcolumntype{C}{>{\\columncolor{meuLaranja}\\centering\\arraybackslash}p{0.26\\linewidth}}
\\newcolumntype{D}{>{\\columncolor{meuAmarelo}\\centering\\arraybackslash}p{0.26\\linewidth}}
\\newcolumntype{E}{>{\\columncolor{meuVerde}\\centering\\arraybackslash}p{0.26\\linewidth}}

% Configurações de tabela (mesmas da C5)
\\renewcommand{\\arraystretch}{1.5}
\\setlength{\\tabcolsep}{6pt}

\\begin{flushleft}
\\noindent
% Título
\\begin{tabularx}{\\linewidth}{>{\\columncolor{schmersalBlue}\\centering\\arraybackslash}X}
    \\textbf{\\textcolor{white}{\\Large Sumário de partes}} \\\\
\\end{tabularx}

\\vspace{0.1cm}

`;

  // Gerar cada parte
  let numeroParte = 1;
  riscosPorParte.forEach((riscosDaParte, parte) => {
    const numeroFormatado = String(numeroParte).padStart(3, '0');

    // Formatar número do perigo com 2 dígitos (01, 02, etc.)
    const numeroPerigo = String(numeroParte).padStart(2, '0');
    const numeroCompleto = referencia ? `${referencia}-${numeroPerigo}` : numeroFormatado;

    // Tabela cinza com descrição da parte e HRN/Classificação (sem SIL)
    latex += `% Tabela com as informações
\\begin{tabularx}{\\linewidth}{>{\\columncolor{gray!20}\\raggedright\\arraybackslash}X>{\\columncolor{gray!20}\\centering\\arraybackslash}p{0.08\\linewidth}>{\\columncolor{gray!20}\\centering\\arraybackslash}p{0.26\\linewidth}}
{\\fontsize{6pt}{7.2pt}\\selectfont\\fontspec{Liberation Sans}
\\textbf{
    ${escapeLatex(numeroCompleto)} - ${escapeLatex(parte)}}}
    & {\\fontsize{8pt}{7.2pt}\\selectfont\\fontspec{Liberation Sans}\\textbf{HRN}} &
    {\\fontsize{8pt}{7.2pt}\\selectfont\\fontspec{Liberation Sans}\\textbf{Classificação}} \\\\
\\end{tabularx}
\\vspace{-0.01cm}

`;

    // Tabela branca com os riscos
    riscosDaParte.forEach((risco, index) => {
      const descricaoRisco = risco.eventoPerigoso || risco.consequencias || 'Risco não especificado';
      const hrn = risco.hrnAtual ?? 0;
      const { classificacao, corFundo, corTexto } = obterClassificacao(hrn);
      const icone = obterIcone(risco.tipoPerigo);

      // Círculo de conformidade: verde preenchido se conforme, vermelho sem preenchimento se não conforme
      const conforme = risco.conformeAtual ?? true;
      const corCirculo = conforme ? 'mygreen' : 'red';
      const preenchimentoCirculo = conforme ? 'mygreen' : 'white';

      // Tipo de coluna para classificação (C, D ou E baseado na cor)
      let tipoColuna = 'C';
      if (corFundo === 'meuAmarelo') {
        tipoColuna = 'D';
      } else if (corFundo === 'meuVerde') {
        tipoColuna = 'E';
      }

      // Se não for o primeiro risco, adicionar espaço antes
      if (index > 0) {
        latex += `\\vspace{-0.2cm}
`;
      }

      latex += `% Tabela com as informações
\\begin{tabularx}{\\linewidth}{>{\\columncolor{white!20}\\raggedright\\arraybackslash}X>{\\columncolor{white!20}\\centering\\arraybackslash}p{0.08\\linewidth}${tipoColuna}}
    \\begin{minipage}[t]{0.98\\linewidth}
    \\small
        \\makebox[0.5cm][l]{%
        \\begin{tikzpicture}[baseline=(current bounding box.center)]
            \\draw[${corCirculo}, thick] (0,0) circle (5pt); % Círculo com borda
            \\fill[${preenchimentoCirculo}] (0,0) circle (4pt); % Centro
            % Desloca a imagem 0.2 para a direita e 0.1 para baixo
            % Imagem comentada - descomente quando os arquivos steam.png, risco.png e pessoas.png estiverem disponíveis
            % \\node[anchor=west] at (0.15,0) {\\includegraphics[width=0.04\\linewidth]{${icone}}}; 
        \\end{tikzpicture}%
        }%
        \\parbox[t]{0.85\\linewidth}{%
        ${escapeLatex(descricaoRisco)}%
        }
    \\end{minipage}
    & 
    \\textbf{${hrn}} & 
    {\\fontsize{6pt}{7.2pt}\\selectfont\\fontspec{Liberation Sans}
    \\textbf{
    \\textcolor{${corTexto}}{${escapeLatex(classificacao)}} }}
\\end{tabularx}

`;
    });

    numeroParte++;
  });

  latex += `\\end{flushleft}

`;

  return latex;
}
